import type { GroupSchemaType, Modification } from '@carboncopyco/types';
import { Button, Loading, TextCaption, notify } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Prompt } from './Prompt';
import { PromptExamples } from './PromptExamples';
import { useAIManagerContext } from '../index';
const MAX_LENGTH = 500;

export interface EditWithAIWithModalProps {
  onResponse: (prompt: string) => Promise<void>;
  onClose: () => void;
  initialPrompt?: string;
  rule?: Modification<GroupSchemaType.aiPromptGroup>;
  pageUrl?: string;
  removeExamples?: boolean;
  inputLabel?: string;
}

export const onAiRequestError = (error: any, prefix = 'database editing') => {
  notify.error(
    error.data?.message ?? 'Failed to generate data. Please try again later.'
  );
  console.error(prefix, 'Failed to generate with ai', error);
};

export const EditWithAIPrompt = ({
  prompt,
  setPrompt,
  promptError,
  setPromptError,
  disabled,
  removeExamples,
  inputLabel,
}: {
  prompt: string;
  setPrompt: (prompt: string) => void;
  promptError: string;
  setPromptError: (text: string) => void;
  disabled?: boolean;
  removeExamples?: boolean;
  inputLabel?: string;
}) => {
  useEffect(() => {
    if (prompt.split(' ').length > MAX_LENGTH) {
      setPromptError(`Max length is ${MAX_LENGTH} words`);
    } else {
      setPromptError(undefined);
    }
  }, [prompt]);
  return (
    <div className="flex flex-col">
      <Prompt
        inputLabel={inputLabel}
        value={prompt}
        onChange={setPrompt}
        textError={promptError}
        disabled={disabled}
      />
      {!removeExamples && <PromptExamples />}
    </div>
  );
};

enum SubmitButtonLabel {
  APPLY = 'Create',
  APPLY_TO_PAGE = 'Apply to page',
  OK = 'OK',
}
export const EditWithAIWithModal = ({
  onClose,
  onResponse,
  initialPrompt,
  rule,
  pageUrl,
  removeExamples,
  inputLabel,
}: EditWithAIWithModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [promptValue, setPromptValue] = useState(initialPrompt ?? '');
  const [promptError, setPromptError] = useState('');
  const isCurrentPage = pageUrl && rule?.content?.pages?.includes(pageUrl);
  const pages = rule?.content?.pages.length;
  const isContentDisabled = !!rule?.content;

  const { getInnerHtml } = useAIManagerContext();

  const submitButtonLabel = useMemo(() => {
    if (isLoading) {
      return '';
    }
    if (isContentDisabled) {
      return isCurrentPage
        ? SubmitButtonLabel.OK
        : SubmitButtonLabel.APPLY_TO_PAGE;
    }
    return SubmitButtonLabel.APPLY;
  }, [isContentDisabled, isCurrentPage, isLoading]);

  const onSubmit = async () => {
    getInnerHtml();
    if (isCurrentPage) {
      onClose();
      return;
    }
    setIsLoading(true);
    try {
      onClose();
      await onResponse(promptValue);
    } catch (error) {
      onAiRequestError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col w-full">
      <EditWithAIPrompt
        inputLabel={inputLabel}
        disabled={isContentDisabled}
        prompt={promptValue}
        setPrompt={setPromptValue}
        promptError={promptError}
        setPromptError={setPromptError}
        removeExamples={removeExamples}
      />
      {pages && (
        <div className="flex gap-0.5 pt-1">
          <TextCaption
            label={`This rule applies to ${pages}`}
            color="text-ds-natural-600"
          />
          <TextCaption
            label="pages"
            underline
            color="text-ds-natural-600"
          />
        </div>
      )}
      <div
        className={classNames(
          'flex justify-end gap-2.5 pt-5',
          isCurrentPage && 'items-center'
        )}
      >
        {!isCurrentPage && (
          <Button
            label="Cancel"
            onClick={onClose}
            highContrast
          />
        )}
        <Button
          style={{ width: 'auto' }}
          disabled={!promptValue || isLoading || !!promptError}
          aria-label={submitButtonLabel}
          label={submitButtonLabel}
          onClick={onSubmit}
          icon={isLoading ? <Loading color="text-ds-accent3-100" /> : undefined}
        />
      </div>
    </div>
  );
};
